import { useEffect, useState } from "react";
import { BankHoliday } from "@jasper/client-models";
import { useOrderConfiguration } from "@jasper/use-cases";

type OrderConfiguration = Pick<
  ReturnType<typeof useOrderConfiguration>,
  "order" | "isLoading" | "getEstimatedDeliveryDate" | "getBankHolidays"
>;

// From the first day of the given month to the last day of the next one, the range shown by the calendar
const getCalendarRange = (date: Date) => {
  const from = new Date(date);
  from.setDate(1);
  from.setHours(0, 0, 0, 0);

  const to = new Date(date);
  to.setMonth(to.getMonth() + 2);
  to.setDate(0);
  to.setHours(23, 59, 59, 999);

  return { from, to };
};

const isSameBankHoliday = (a: BankHoliday, b: BankHoliday) =>
  a.startDate === b.startDate && a.endDate === b.endDate && a.name === b.name;

// Delivery date selection: the estimated optimal and express dates, the selected date
// (the one already saved on the order, otherwise the optimal one) and the bank holidays shown in the calendar.
export const useDeliveryDate = ({
  order,
  isLoading,
  getEstimatedDeliveryDate,
  getBankHolidays,
}: OrderConfiguration) => {
  const [deliveryDate, setDeliveryDate] = useState<Date>();
  const [estimatedOptimalDeliveryDate, setEstimatedOptimalDeliveryDate] =
    useState<Date>();
  const [estimatedRushDeliveryDate, setEstimatedRushDeliveryDate] =
    useState<Date>();
  const [hasEstimationFailed, setHasEstimationFailed] = useState(false);
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);

  const savedDeliveryDate = order?.deliveryDate;

  // Only depends on the saved date: the query functions are recreated on every render
  useEffect(() => {
    if (isLoading) {
      return;
    }

    let isActive = true;

    // A date already chosen by the user is never overwritten
    if (savedDeliveryDate) {
      setDeliveryDate(current => current ?? new Date(savedDeliveryDate));
    }

    getEstimatedDeliveryDate()
      .then(estimation => {
        if (!isActive) return;

        const optimal = new Date(estimation.estimatedOptimalDeliveryDate);
        setEstimatedOptimalDeliveryDate(optimal);
        setEstimatedRushDeliveryDate(
          new Date(estimation.estimatedRushDeliveryDate)
        );
        setDeliveryDate(current => current ?? optimal);
        setHasEstimationFailed(false);
      })
      .catch(error => {
        if (!isActive) return;

        console.error("Error while estimating the delivery dates", error);
        setHasEstimationFailed(true);
      });

    return () => {
      isActive = false;
    };
  }, [savedDeliveryDate, isLoading]);

  // Bank holidays only grey out calendar days, a failure is logged but doesn't block the page
  const loadBankHolidays = (date: Date) => {
    const { from, to } = getCalendarRange(date);

    getBankHolidays(from, to)
      .then(newBankHolidays => {
        // Merged with the already fetched ones so navigating between months doesn't lose any
        setBankHolidays(current => [
          ...newBankHolidays,
          ...current.filter(
            bankHoliday =>
              !newBankHolidays.some(newBankHoliday =>
                isSameBankHoliday(newBankHoliday, bankHoliday)
              )
          ),
        ]);
      })
      .catch(error => {
        console.error("Error while fetching bank holidays", error);
      });
  };

  useEffect(() => {
    loadBankHolidays(new Date());
  }, []);

  return {
    deliveryDate,
    setDeliveryDate,
    estimatedOptimalDeliveryDate,
    estimatedRushDeliveryDate,
    hasEstimationFailed,
    bankHolidays,
    handleMonthChange: loadBankHolidays,
  };
};
